import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface TaskSuggestion {
  description: string;
  category: string;
  estimatedDuration: number;
  details: string;
}

export function useTaskSuggestions() {
  const [suggestions, setSuggestions] = useState<TaskSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const getSuggestions = async (input: string) => {
    if (!input || input.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-task-options', {
        body: { input }
      });

      if (error) {
        console.error('Error getting task suggestions:', error);
        // Don't show error toast for rate limits, just clear suggestions
        if (error.message?.includes('429') || error.message?.includes('402')) {
          setSuggestions([]);
          return;
        }
        throw error;
      }

      if (data?.suggestions && Array.isArray(data.suggestions)) {
        setSuggestions(data.suggestions);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Failed to get task suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearSuggestions = () => {
    setSuggestions([]);
  };

  return {
    suggestions,
    isLoading,
    getSuggestions,
    clearSuggestions,
  };
}